import React, { useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QuickBuyFormInput } from "./QuickbuyFormInput";
import { useSelectedPlanContext } from "../../../contexts/SelectedPlanContext";

// Map display labels to keys used in selectedPlan.
const keyMapping = {
	CPU: "cpu",
	RAM: "ram",
	STORAGE: "storage",
	BANDWIDTH: "bandwidth", // IMPORTANT : backend returns "bandwidth"
	TRANSFER: "transfer",
	ENERGY: "energy",
	PREFIX: "prefix",
	RACK: "rack",
	NAS: "nas",
};

const InputWithOptions = ({ input, resources = [], translations, restrictedIds = [], vertical = false }) => {
	const { selectedPlan, changeSelectedPlan } = useSelectedPlanContext();

	// Store the last non-empty resources as a fallback.
	const lastResourcesRef = useRef(resources);
	useEffect(() => {
		if (resources && resources.length > 0) {
			lastResourcesRef.current = resources;
		}
	}, [resources]);
	const effectiveResources = resources && resources.length > 0 ? resources : lastResourcesRef.current || [];

	// console.log(`InputWithOptions (${input}) - effectiveResources:`, effectiveResources);

	// Memoize sorted resources.
	const sortedArray = useMemo(() => {
		// console.log(`Sorting resources for ${input}:`, effectiveResources);
		return [...effectiveResources].sort((a, b) => a.order - b.order);
	}, [effectiveResources]);

	// Helper function: check if an option has dependencies.
	const inputHasDependencies = (option) => {
		if (!option.dependencies?.length) return false;
		return !option.dependencies.some((depId) =>
			Object.values(selectedPlan).some((selectedResource) => selectedResource?.id === depId)
		);
	};

	// Helper function: check if an option is restricted.
	const inputIsRestricted = (option) => restrictedIds?.includes(option.id);

	useEffect(() => {
		const key = keyMapping[input];
		// console.log(key);

		// console.log(selectedPlan);

		const current = selectedPlan?.[key];
		// console.log(current);

		// if they’ve picked something now invalid, pick a fallback
		if (current && (inputHasDependencies(current) || inputIsRestricted(current))) {
			// try “in_plan” first
			let fallback = sortedArray.find(
				(opt) => opt.in_plan && !inputHasDependencies(opt) && !inputIsRestricted(opt)
			);
			// otherwise the first fully valid option
			if (!fallback) {
				fallback = sortedArray.find((opt) => !inputHasDependencies(opt) && !inputIsRestricted(opt));
			}
			if (fallback) {
				changeSelectedPlan((prev) => ({
					...prev,
					[key]: {
						id: fallback.id,
						value: fallback.value,
						order: fallback.order,
						in_plan: fallback.in_plan,
					},
				}));
			}
		}
		// Additionally, if no selection exists at all and we have options, select the first valid one
		else if ((!current || !current.id || current.id === "") && sortedArray.length > 0) {
			console.log(`InputWithOptions: No selection for ${input}, finding default`, {
				current,
				availableOptions: sortedArray.length,
				restrictedIds,
			});

			// try "in_plan" first
			let defaultOption = sortedArray.find(
				(opt) => opt.in_plan && !inputHasDependencies(opt) && !inputIsRestricted(opt)
			);
			// otherwise the first fully valid option
			if (!defaultOption) {
				defaultOption = sortedArray.find((opt) => !inputHasDependencies(opt) && !inputIsRestricted(opt));
			}
			// fallback to first option if no other choice
			if (!defaultOption && sortedArray.length > 0) {
				defaultOption = sortedArray[0];
			}

			if (defaultOption) {
				console.log(`InputWithOptions: Setting default for ${input}`, defaultOption);
				changeSelectedPlan((prev) => ({
					...prev,
					[key]: {
						id: defaultOption.id,
						value: defaultOption.value,
						order: defaultOption.order,
						in_plan: defaultOption.in_plan || false,
					},
				}));
			} else {
				console.warn(`InputWithOptions: No valid default found for ${input}`);
			}
		}
	}, [restrictedIds, sortedArray, selectedPlan, input, changeSelectedPlan]);

	// Animation variants for the list and list items.
	const listVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
	};

	const itemVariants = {
		hidden: { opacity: 0, y: -10 },
		visible: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: 10 },
	};

	return (
		<div className="flex flex-col gap-3 rounded-xl w-full">
			<p
				className={`${
					vertical ? "self-center" : "self-start"
				} flex gap-2 items-center font-semibold text-xs uppercase`}
			>
				{translations[input]}
			</p>
			<AnimatePresence>
				<motion.ul
					className={`flex ${vertical ? "flex-col" : "flex-row"} w-full gap-2.5 items-center justify-center`}
					variants={listVariants}
					initial="hidden"
					animate="visible"
				>
					{sortedArray.map((option) => (
						<motion.li
							key={option.id}
							className="w-full"
							variants={itemVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
						>
							<QuickBuyFormInput
								input={input.toLowerCase()}
								inputData={option}
								isQuickBuy={!vertical}
								translations={translations}
								resources={resources}
								disabled={inputIsRestricted(option)}
							/>
						</motion.li>
					))}
				</motion.ul>
			</AnimatePresence>
		</div>
	);
};

export default InputWithOptions;
